import type { GenerateMetadataFunction } from 'expo-router/server';
import { fieldMetadataProps } from '../../../../lib/i18n/fieldMetadataProps';
import { staticPageMetadata } from '../../../../lib/i18n/staticPageMetadata';
import { getStaticBuildLocale } from '../../../../lib/i18n/locales';
import { useLocalSearchParams, Link } from 'expo-router';
import { View, Text, StyleSheet, ActivityIndicator, ScrollView, TouchableOpacity, Platform } from 'react-native';
import { useState, useEffect } from 'react';
import { supabase, toError } from '../../../../lib/supabase';
import type { Database } from '../../../../lib/database.types';
import Header from '../../../../components/Header';
import Footer from '../../../../components/Footer';
import MetaTags from '../../../../components/MetaTags';
import colors from '../../../../lib/colors';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { resolveMessage } from '../../../../lib/i18n/messages';
import { overlayRuntimeTaxonomyRow } from '../../../../lib/i18n/taxonomy';
import { useAppLocale } from '../../../../lib/i18n/AppI18nProvider';


// Import the static domains hierarchy data
let staticDomainsHierarchy: any[] = [];
try {
    staticDomainsHierarchy = require('../../../../assets/domains-hierarchy.json');
    console.log('📄 Loaded static domains hierarchy:', staticDomainsHierarchy.length);
} catch (e) {
    console.log('📄 No static domains hierarchy found');
}

type Field = Database['public']['Tables']['fields']['Row'];
type Subfield = Database['public']['Tables']['subfields']['Row'];

// Generate static params at build time for all field pages
export async function generateStaticParams(): Promise<Record<string, string>[]> {
    try {
        // Use the pre-fetched static data
        const domains = require('../../../../assets/domains-hierarchy.json');
        
        // Generate params for each field across all domains
        const params: Record<string, string>[] = [];
        domains.forEach((domain: any) => {
            domain.fields.forEach((field: any) => {
                params.push({
                    id: field.id.toString(),
                    slug: field.slug
                });
            });
        });
        
        return params;
    } catch (error) {
        console.error('Error in generateStaticParams:', error);
        // Return empty array if there's an error to prevent build failure
        return [];
    }
}

export const generateMetadata: GenerateMetadataFunction = (_request, params) => {
    const field = staticDomainsHierarchy.flatMap((domain) => domain.fields)
        .find((candidate) => String(candidate.id) === String(params.id));
    if (!field) return { robots: 'noindex, nofollow' };
    const localized = overlayRuntimeTaxonomyRow(field, 'field', getStaticBuildLocale());
    return staticPageMetadata(fieldMetadataProps(localized));
};

export default function FieldDetail() {
    const { i18n } = useLingui();
    const { messageLocale } = useAppLocale();
    const { id } = useLocalSearchParams<{ id: string }>();
    
    // Try to find field in static data first
    let staticField: any = null;
    let staticSubfields: any[] = [];
    
    if (id) {
        // Search through all domains to find the field
        for (const domain of staticDomainsHierarchy) {
            const field = domain.fields.find((f: any) => f.id.toString() === id);
            if (field) {
                staticField = overlayRuntimeTaxonomyRow(field, 'field', messageLocale);
                staticSubfields = (field.subfields || []).map((subfield: Subfield) => (
                    overlayRuntimeTaxonomyRow(subfield, 'subfield', messageLocale)
                ));
                break;
            }
        }
    }

    const [field, setField] = useState<Field | null>(staticField);
    const [subfields, setSubfields] = useState<Subfield[]>(staticSubfields);
    const [loading, setLoading] = useState(!staticField); // Only load if no static data
    const [loadFailed, setLoadFailed] = useState(false);

    useEffect(() => {
        // Only fetch if we're on the client and don't have static data for this field
        if (id && !staticField && Platform.OS === 'web' && typeof window !== 'undefined') {
            fetchFieldData();
        }
    }, [id]);

    const fetchFieldData = async () => {
        setLoading(true);
        try {
            // Fetch field data
            const { data: fieldData, error: fieldError } = await supabase
                .from('fields')
                .select('*')
                .eq('id', parseInt(id!))
                .single();

            if (fieldError) throw toError(fieldError);
            setField(overlayRuntimeTaxonomyRow(fieldData, 'field', messageLocale));

            // Fetch subfields
            const { data: subfieldData, error: subfieldError } = await supabase
                .from('subfields')
                .select('*')
                .eq('field_id', parseInt(id!))
                .order('name', { ascending: true });

            if (subfieldError) throw toError(subfieldError);
            setSubfields((subfieldData || []).map((subfield) => (
                overlayRuntimeTaxonomyRow(subfield, 'subfield', messageLocale)
            )));

        } catch (err: any) {
            console.error('Error fetching field data:', err);
            setLoadFailed(true);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <View style={styles.page}>
                <Header />
                <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color={colors.primaryGreen} />
                </View>
            </View>
        );
    }

    if (loadFailed || !field) {
        return (
            <View style={styles.page}>
                <Header />
                <View style={styles.contentContainer}>
                    <Link href="/fields" asChild>
                        <TouchableOpacity style={styles.backLink}>
                            <Text style={styles.backLinkText}><Trans id="taxonomy.field.back">← Back</Trans></Text>
                        </TouchableOpacity>
                    </Link>
                    <View style={styles.errorContainer}>
                        <Text style={styles.errorText}>
                            {loadFailed
                                ? i18n._(msg({ id: 'taxonomy.field.loadError', message: 'Failed to load field data' }))
                                : i18n._(msg({ id: 'taxonomy.field.notFound', message: 'Field not found' }))}
                        </Text>
                    </View>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.page}>
            {field && (
                <MetaTags {...fieldMetadataProps(field)} />
            )}
            <Header />
            <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} showsVerticalScrollIndicator={false}>
                <View style={styles.contentContainer}>
                    <Link href="/fields" asChild>
                        <TouchableOpacity style={styles.backLink}>
                            <Text style={styles.backLinkText}><Trans id="taxonomy.field.back">← Back</Trans></Text>
                        </TouchableOpacity>
                    </Link>

                    {/* Field Header */}
                    <View style={styles.fieldHeader}>
                        <Text 
                            style={styles.fieldTitle}
                            accessibilityRole="header"
                            {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 1 })}
                        >
                            {resolveMessage(i18n, msg({
                                id: 'taxonomy.field.title',
                                message: '{fieldName} Papers',
                            }), { fieldName: field.name })}
                        </Text>
                        {field.description && (
                            <Text style={styles.fieldDescription}>{field.description}</Text>
                        )}
                        <View style={styles.fieldHeaderBottom}>
                            <View style={{ flex: 1 }} />
                            {field.works_count !== null && field.works_count > 0 && (
                                <View style={styles.fieldBadge}>
                                    <Text style={styles.fieldBadgeText}>
                                        {i18n._({
                                            ...msg({
                                                id: 'taxonomy.field.paperCount',
                                                message: '{count, plural, one {# paper} other {# papers}}',
                                            }),
                                            values: { count: field.works_count },
                                        })}
                                    </Text>
                                </View>
                            )}
                        </View>
                    </View>

                    {/* Subfields Section */}
                    <View style={styles.subfieldsSection}>
                        <Text 
                            style={styles.sectionTitle}
                            accessibilityRole="header"
                            {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 2 })}
                        >
                            {resolveMessage(i18n, msg({
                                id: 'taxonomy.field.specializations',
                                message: '{fieldName} Specializations',
                            }), { fieldName: field.name })}
                        </Text>
                        {subfields.length === 0 ? (
                            <Text style={styles.noSubfields}>
                                <Trans id="taxonomy.field.noSpecializations">No specializations available for this field.</Trans>
                            </Text>
                        ) : (
                            <View style={styles.subfieldsGrid}>
                                {subfields.map((subfield) => (
                                    <Link
                                        key={subfield.id}
                                        href={`/subfield/${field.id}/${field.slug}/${subfield.id}/${subfield.slug}`}
                                        asChild
                                    >
                                        <TouchableOpacity
                                            style={styles.subfieldCard}
                                        >
                                        <View style={styles.subfieldCardHeader}>
                                            <Text 
                                                style={styles.subfieldName}
                                                accessibilityRole="header"
                                                {...(Platform.OS === 'web' && { role: 'heading', 'aria-level': 3 })}
                                            >
                                                {subfield.name}
                                            </Text>
                                            {subfield.works_count !== null && (
                                                <View style={styles.subfieldBadge}>
                                                    <Text style={styles.subfieldBadgeText}>
                                                        {i18n._({
                                                            ...msg({
                                                                id: 'taxonomy.field.paperCount',
                                                                message: '{count, plural, one {# paper} other {# papers}}',
                                                            }),
                                                            values: { count: subfield.works_count },
                                                        })}
                                                    </Text>
                                                </View>
                                            )}
                                        </View>
                                        {subfield.description && (
                                            <Text style={styles.subfieldDescription} numberOfLines={2}>
                                                {subfield.description}
                                            </Text>
                                        )}
                                    </TouchableOpacity>
                                    </Link>
                                ))}
                            </View>
                        )}
                    </View>
                </View>
                <Footer />
            </ScrollView>
        </View>
    );
}

const styles = StyleSheet.create({
    page: {
        flex: 1,
        backgroundColor: '#fafafa',
    },
    contentContainer: {
        width: '100%',
        maxWidth: 900,
        alignSelf: 'center',
        paddingHorizontal: 20,
        paddingTop: 20,
        paddingBottom: 20,
        flexGrow: 1,
    },
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
    },
    scrollView: {
        flex: 1,
    },
    scrollViewContent: {
        flexGrow: 1,
    },
    backLink: {
        marginBottom: 24,
    },
    backLinkText: {
        fontSize: 16,
        color: colors.primaryGreen,
        fontWeight: '500',
    },
    errorContainer: {
        backgroundColor: '#fef2f2',
        borderWidth: 1,
        borderColor: '#fecaca',
        borderRadius: 8,
        padding: 20,
        marginBottom: 20,
    },
    errorText: {
        color: '#dc2626',
        fontSize: 16,
        textAlign: 'center',
    },
    fieldHeader: {
        backgroundColor: 'white',
        borderRadius: 12,
        padding: 24,
        marginBottom: 32,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    fieldTitle: {
        fontSize: 28,
        fontWeight: 'bold',
        color: colors.primaryGreenDark,
        marginBottom: 12,
    },
    fieldDescription: {
        fontSize: 16,
        color: '#4b5563',
        lineHeight: 24,
        marginBottom: 12,
    },
    fieldHeaderBottom: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    fieldBadge: {
        backgroundColor: '#f9fafb',
        borderRadius: 6,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    fieldBadgeText: {
        fontSize: 12,
        color: '#9ca3af',
        fontWeight: '600',
    },
    subfieldsSection: {
        marginBottom: 40,
    },
    sectionTitle: {
        fontSize: 22,
        fontWeight: '600',
        color: colors.primaryGreenDark,
        marginBottom: 20,
    },
    noSubfields: {
        fontSize: 16,
        color: '#6b7280',
        fontStyle: 'italic',
    },
    subfieldsGrid: {
        marginTop: 16,
    },
    subfieldCard: {
        backgroundColor: 'white',
        borderRadius: 8,
        padding: 16,
        marginBottom: 12,
        borderWidth: 1,
        borderColor: '#e5e7eb',
        ...(Platform.OS === 'web' && {
            cursor: 'pointer',
            transition: 'all 0.2s',
            ':hover': {
                borderColor: colors.primaryGreen,
                backgroundColor: '#f9fdfb',
            },
        }),
    },
    subfieldCardHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 4,
    },
    subfieldName: {
        fontSize: 18,
        fontWeight: '500',
        color: colors.primaryGreenDark,
        flex: 1,
        paddingRight: 8,
    },
    subfieldBadge: {
        backgroundColor: '#f9fafb',
        borderRadius: 6,
        paddingHorizontal: 6,
        paddingVertical: 2,
        marginLeft: 8,
        borderWidth: 1,
        borderColor: '#e5e7eb',
    },
    subfieldBadgeText: {
        fontSize: 11,
        color: '#9ca3af',
        fontWeight: '600',
    },
    subfieldDescription: {
        fontSize: 14,
        color: '#6b7280',
        lineHeight: 20,
    },
});
